import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, Edit2, Trash2, Eye, Calendar, Tag, 
  Image as ImageIcon, Search, Filter, ArrowLeft
} from 'lucide-react';

const BlogManagement = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    scheduled: 0
  });
  const [bannerAd, setBannerAd] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerLinkUrl, setBannerLinkUrl] = useState('');
  const [showBannerSection, setShowBannerSection] = useState(false);
  
  // Category management state
  const [showCategorySection, setShowCategorySection] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  // Helper to construct full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // Handle if url is an object (thumbnail object)
    if (typeof url === 'object' && url.url) {
      url = url.url;
    }
    
    // SIMPLE: Just return the URL as-is
    // Upload endpoint now returns full URLs
    return url;
  };

  // Helper to get blog post URL with category structure
  const getPostUrl = (post) => {
    if (post.primary_category) {
      const category = categories.find(c => c.id === post.primary_category);
      if (category) {
        return `/blog/${category.slug}/${post.slug}`;
      }
    }
    return `/blog/${post.slug}`;
  };

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchBannerAd();
  }, [filterStatus]);

  const fetchPosts = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      
      const response = await api.get('/admin/blog/posts', { params });
      setPosts(response.data.posts || []);
      
      // Calculate stats
      const allPosts = response.data.posts || [];
      setStats({
        total: allPosts.length,
        published: allPosts.filter(p => p.status === 'published').length,
        draft: allPosts.filter(p => p.status === 'draft').length,
        scheduled: allPosts.filter(p => p.status === 'scheduled').length
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blog/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchBannerAd = async () => {
    try {
      const response = await api.get('/blog/banner-ad');
      setBannerAd(response.data);
      setBannerLinkUrl(response.data.link_url || '');
    } catch (error) {
      console.error('Failed to fetch banner ad:', error);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBanner(true);
    try {
      // Upload image
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', 'Sidebar Banner Ad');

      const uploadResponse = await api.post('/admin/blog/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update banner ad
      const bannerData = {
        image_url: uploadResponse.data.url,
        link_url: bannerLinkUrl || '',
        alt_text: 'Sidebar Banner Ad',
        enabled: true
      };

      const updateResponse = await api.put('/admin/blog/banner-ad', bannerData);
      setBannerAd(updateResponse.data);
      alert('Banner ad uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload banner ad:', error);
      alert('Failed to upload banner ad');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleBannerLinkUpdate = async () => {
    if (!bannerAd?.image_url) {
      alert('Please upload a banner image first');
      return;
    }

    try {
      const bannerData = {
        image_url: bannerAd.image_url,
        link_url: bannerLinkUrl,
        alt_text: bannerAd.alt_text || 'Sidebar Banner Ad',
        enabled: bannerAd.enabled
      };

      const response = await api.put('/admin/blog/banner-ad', bannerData);
      setBannerAd(response.data);
      alert('Banner link updated successfully!');
    } catch (error) {
      console.error('Failed to update banner link:', error);
      alert('Failed to update banner link');
    }
  };

  const handleRemoveBanner = async () => {
    if (!window.confirm('Are you sure you want to remove the banner ad?')) return;

    try {
      const bannerData = {
        image_url: null,
        link_url: null,
        alt_text: 'Advertisement',
        enabled: false
      };

      const response = await api.put('/admin/blog/banner-ad', bannerData);
      setBannerAd(response.data);
      setBannerLinkUrl('');
      alert('Banner ad removed successfully!');
    } catch (error) {
      console.error('Failed to remove banner ad:', error);
      alert('Failed to remove banner ad');
    }
  };


  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await api.delete(`/admin/blog/posts/${postId}`);
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    setAddingCategory(true);
    try {
      await api.post('/admin/blog/categories', {
        name: newCategoryName
      });
      setNewCategoryName('');
      fetchCategories();
      alert('Category added successfully!');
    } catch (error) {
      console.error('Failed to add category:', error);
      alert('Failed to add category');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await api.delete(`/admin/blog/categories/${categoryId}`);
      fetchCategories();
      alert('Category deleted successfully!');
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Failed to delete category');
    }
  };

  const handleCleanupTestData = async () => {
    const confirmed = window.confirm(
      'This will delete ALL test posts and test categories. This action cannot be undone. Are you sure?'
    );
    
    if (!confirmed) return;

    try {
      const response = await api.post('/admin/blog/cleanup-test-data');
      alert(`Cleanup complete!\nPosts deleted: ${response.data.posts_deleted}\nCategories deleted: ${response.data.categories_deleted}`);
      fetchPosts();
      fetchCategories();
    } catch (error) {
      console.error('Failed to cleanup test data:', error);
      alert('Failed to cleanup test data');
    }
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      archived: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB2F5B] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
              <p className="text-gray-600 mt-1">Create and manage your blog posts</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowBannerSection(!showBannerSection)}
              variant="outline"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Sidebar Banner Ad
            </Button>
            <Button
              onClick={() => setShowCategorySection(!showCategorySection)}
              variant="outline"
            >
              <Tag className="h-4 w-4 mr-2" />
              Manage Categories
            </Button>
            <Button
              onClick={() => navigate('/admin/blog/new')}
              className="bg-[#EB2F5B] hover:bg-[#d02850]"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Posts</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">{stats.published}</div>
              <div className="text-sm text-gray-600">Published</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
              <div className="text-sm text-gray-600">Drafts</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
              <div className="text-sm text-gray-600">Scheduled</div>
            </CardContent>
          </Card>
        </div>


        {/* Category Management Section */}
        {showCategorySection && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Category Management</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Create and manage blog categories
              </p>
            </CardHeader>
            <CardContent>
              {/* Add New Category */}
              <div className="mb-6">
                <Label>Add New Category</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    type="text"
                    placeholder="Category name..."
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  />
                  <Button
                    onClick={handleAddCategory}
                    disabled={addingCategory || !newCategoryName.trim()}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Existing Categories */}
              <div>
                <Label className="mb-3 block">Existing Categories ({categories.length})</Label>
                {categories.length === 0 ? (
                  <p className="text-gray-500 text-sm">No categories yet. Add your first category above.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                      >
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-gray-600" />
                          <span className="text-sm font-medium">{category.name}</span>
                        </div>
                        <Button
                          onClick={() => handleDeleteCategory(category.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cleanup Test Data */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                        Pre-Deployment Cleanup
                      </h4>
                      <p className="text-xs text-yellow-800">
                        Remove all test posts and test categories before deploying to production. 
                        This will delete any posts or categories with "test" in the title/name.
                      </p>
                    </div>
                    <Button
                      onClick={handleCleanupTestData}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clean Test Data
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Banner Ad Section */}
        {showBannerSection && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Sidebar Banner Ad</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Upload a banner ad that will appear below "Related Posts" on all blog articles. 
                The banner will auto-scale to fit the sidebar width with rounded corners.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Upload & Preview */}
                <div>
                  <Label>Banner Image</Label>
                  {bannerAd?.image_url && bannerAd.image_url.trim() !== '' ? (
                    <div className="mt-2">
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={getImageUrl(bannerAd.image_url)}
                          alt={bannerAd.alt_text}
                          className="w-full h-auto"
                        />
                      </div>
                      <Button
                        onClick={handleRemoveBanner}
                        variant="outline"
                        size="sm"
                        className="mt-3 w-full text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove Banner
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <label className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#EB2F5B] transition-colors">
                          <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                          <p className="text-sm text-gray-600 mb-1">
                            {uploadingBanner ? 'Uploading...' : 'Click to upload banner image'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Recommended: Tall vertical image
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBannerUpload}
                          className="hidden"
                          disabled={uploadingBanner}
                        />
                      </label>
                    </div>
                  )}
                </div>

                {/* Right: Link URL */}
                <div>
                  <Label>Banner Link URL</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Where should the banner ad link to when clicked?
                  </p>
                  <Input
                    type="url"
                    placeholder="https://example.com/landing-page"
                    value={bannerLinkUrl}
                    onChange={(e) => setBannerLinkUrl(e.target.value)}
                    className="mb-3"
                  />
                  <Button
                    onClick={handleBannerLinkUpdate}
                    disabled={!bannerAd?.image_url}
                    className="w-full bg-[#EB2F5B] hover:bg-[#d02850]"
                  >
                    Update Banner Link
                  </Button>

                  {bannerAd?.image_url && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-semibold">✓ Banner Active</p>
                      <p className="text-xs text-green-700 mt-1">
                        This banner is now showing on all blog articles
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search posts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('all')}
                  size="sm"
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === 'published' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('published')}
                  size="sm"
                >
                  Published
                </Button>
                <Button
                  variant={filterStatus === 'draft' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('draft')}
                  size="sm"
                >
                  Drafts
                </Button>
                <Button
                  variant={filterStatus === 'scheduled' ? 'default' : 'outline'}
                  onClick={() => setFilterStatus('scheduled')}
                  size="sm"
                >
                  Scheduled
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Posts List */}
        {filteredPosts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No posts found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Try adjusting your search' : 'Create your first blog post to get started'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => navigate('/admin/blog/new')}
                  className="bg-[#EB2F5B] hover:bg-[#d02850]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Post
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {/* Thumbnail */}
                    {post.thumbnail?.url ? (
                      <div className="w-32 h-32 flex-shrink-0">
                        <img
                          src={getImageUrl(post.thumbnail.url)}
                          alt={post.thumbnail.alt_text || post.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-32 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-1">
                            {post.title}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <span>{getStatusBadge(post.status)}</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {post.published_at 
                                ? new Date(post.published_at).toLocaleDateString()
                                : new Date(post.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {post.view_count} views
                            </span>
                            {post.featured && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold">
                                Featured
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          {post.status === 'published' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(getPostUrl(post), '_blank')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(post.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Excerpt */}
                      {post.excerpt && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                      )}

                      {/* Meta */}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {post.categories && post.categories.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            <span>{post.categories.length} {post.categories.length === 1 ? 'category' : 'categories'}</span>
                          </div>
                        )}
                        <span>By {post.author_name}</span>
                        <span className="font-mono text-xs">{getPostUrl(post)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogManagement;
